import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Loader2, Users, Coins, TrendingDown, Wallet } from "lucide-react";

const TABS = [["executive", "Executive"], ["programs", "Programs"], ["customers", "Customers"]];

function Kpi({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p><Icon className={`h-5 w-5 ${color}`} /></div>
      <p className="text-2xl font-bold text-foreground mt-3">{value}</p>
    </div>
  );
}

function Dist({ title, data, testid }) {
  const entries = Object.entries(data || {});
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {entries.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No data</p> : (
        <div className="space-y-3">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-sm mb-1"><span className="text-foreground">{k}</span><span className="text-muted-foreground font-semibold">{v}</span></div>
              <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LoyaltyAnalyticsPage() {
  const { format } = useCurrency();
  const [tab, setTab] = useState("executive");
  const [exec, setExec] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p, c] = await Promise.all([loyaltyService.analyticsExecutive(), loyaltyService.analyticsPrograms(), loyaltyService.analyticsCustomers()]);
      setExec(e); setPrograms(p); setCustomers(c);
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const programColumns = [
    { key: "program", header: "Program", render: (r) => <span className="font-medium text-foreground">{r.program}</span> },
    { key: "type", header: "Type", render: (r) => <Badge color="blue">{r.type}</Badge> },
    { key: "transactions", header: "Transactions", render: (r) => r.transactions },
    { key: "earnTxns", header: "Earn", render: (r) => r.earnTxns },
    { key: "redeemTxns", header: "Redeem", render: (r) => r.redeemTxns },
    { key: "redemptionRate", header: "Redemption %", render: (r) => `${r.redemptionRate}%` },
  ];
  const custColumns = [
    { key: "customerId", header: "Customer", render: (r) => <span className="font-mono text-sm">{r.customerId}</span> },
    { key: "score", header: "Loyalty Score", render: (r) => <span className="font-semibold">{r.score}</span> },
    { key: "tier", header: "Tier", render: (r) => r.tier || "—" },
    { key: "lifetimeEarned", header: "Lifetime Earned", render: (r) => (r.lifetimeEarned || 0).toLocaleString() },
  ];

  return (
    <div data-testid="loyalty-analytics-page">
      <PageHeader title="Loyalty Analytics" subtitle="Executive, program & customer loyalty intelligence" />

      <div className="flex gap-1 mb-5 border-b border-border">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`analytics-tab-${k}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <>
          {tab === "executive" && exec && (
            <div data-testid="analytics-executive">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Kpi testid="exec-active-members" label="Active Members" value={`${exec.activeMembers}/${exec.totalMembers}`} icon={Users} color="text-sky-500" />
                <Kpi testid="exec-reward-value" label="Reward Value Delivered" value={format(exec.rewardValueDelivered)} icon={Coins} color="text-emerald-500" />
                <Kpi testid="exec-liability" label="Outstanding Liability" value={format(exec.estimatedLiability)} icon={Wallet} color="text-violet-500" />
                <Kpi testid="exec-redemption" label="Redemption Rate" value={`${exec.redemptionRate}%`} icon={TrendingDown} color="text-amber-500" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <Kpi testid="exec-issued" label="Points Issued" value={(exec.pointsIssued || 0).toLocaleString()} icon={Coins} color="text-emerald-500" />
                <Kpi testid="exec-redeemed" label="Points Redeemed" value={(exec.pointsRedeemed || 0).toLocaleString()} icon={Coins} color="text-rose-500" />
                <Kpi testid="exec-outstanding" label="Points Outstanding" value={(exec.pointsOutstanding || 0).toLocaleString()} icon={Coins} color="text-violet-500" />
              </div>
              <Dist title="Tier Distribution" data={exec.tierDistribution} testid="exec-tier-dist" />
            </div>
          )}

          {tab === "programs" && programs && (
            <DataTable testid="analytics-programs-table" rows={programs.programs} loading={false} columns={programColumns} empty="No programs yet." />
          )}

          {tab === "customers" && customers && (
            <div data-testid="analytics-customers" className="space-y-6">
              <Dist title="Loyalty Score Distribution" data={customers.scoreDistribution} testid="cust-score-dist" />
              <div>
                <h3 className="font-semibold text-foreground mb-3">Top Customers</h3>
                <DataTable testid="analytics-top-customers-table" rows={customers.topCustomers} loading={false} columns={custColumns} empty="No customers yet." />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
